import styled, { keyframes } from "styled-components";
import { fonts } from "../../../../styles/theme";

/* ===== Animação suave ===== */
const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(12px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

/* ===== Container principal ===== */
export const TopicsContainer = styled.div`
  width: 100%;
  max-width: 1200px;
  min-height: 10vh;
  margin: 1.5% auto 0;
  display: flex;
  justify-content: center;
  align-items: stretch;
  flex-direction: column;
  padding: 1rem;
  background: transparent;
  border: 1px solid var(--border-default);
  border-radius: 16px;
  box-shadow: var(--shadow-md);
  overflow: hidden;
  box-sizing: border-box;
  animation: ${fadeIn} 0.4s ease-out;

  @media screen and (max-width: 768px) {
    margin: 0.5rem 0 0;
    padding: 0.75rem;
    border-radius: 12px;
    max-width: 100%;
  }
`;

export const ScreenTitle = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 2rem auto 1.5rem auto;
  max-width: 900px;
  font-size: 22px;
  font-weight: 700;
  font-family: ${fonts.primary};
  letter-spacing: 0.5px;

  @media screen and (max-width: 768px) {
    margin: 0.75rem auto 1rem;
    font-size: 1.05rem;
  }
`;

export const StyledSpan = styled.span`
  color: var(--text-primary);
  font-size: 1.65rem;
  font-weight: 700;
  margin: 0.5rem 0 0.25rem 0;
  text-shadow: 0 4px 20px
    color-mix(in srgb, var(--accent-secondary) 30%, transparent);
  text-align: left;
  line-height: 1.2;

  @media screen and (max-width: 768px) {
    font-size: 1.15rem;
  }
`;

export const TopicList = styled.div`
  width: 100%;
  max-width: 900px;
  margin: 1.5rem auto 2rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  box-sizing: border-box;

  @media screen and (max-width: 768px) {
    margin: 0.75rem 0 1.25rem;
  }
`;

export const TopicCard = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem;
  border-radius: 14px;
  background: var(--surface-glass);
  backdrop-filter: blur(14px);
  border: 1px solid var(--border-subtle);
  cursor: pointer;
  transition: all 0.25s ease;
  border-color: ${({ $active }) =>
    $active ? "var(--accent)" : "var(--border-subtle)"};
  box-sizing: border-box;
  min-width: 0;

  &:hover {
    transform: translateX(4px);
    border-color: var(--accent);
  }

  @media screen and (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    padding: 0.85rem;
    border-radius: 12px;

    &:hover {
      transform: none;
    }
  }
`;

export const TopicInfo = styled.div`
  width: 60%;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex: 1;
  min-width: 0;

  @media screen and (max-width: 768px) {
    width: 100%;
  }
`;

export const TopicIndex = styled.div`
  min-width: 35px;
  height: 35px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: linear-gradient(
    135deg,
    var(--accent-soft),
    var(--border-default)
  );
  color: var(--brand-navy);
  font-weight: 700;
  font-size: 0.8rem;
  flex-shrink: 0;
`;

export const TopicText = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  min-width: 0;
`;

export const TopicTitle = styled.span`
  color: var(--accent-secondary);
  font-size: 0.9rem;
  font-weight: 600;
  overflow-wrap: anywhere;

  @media screen and (max-width: 768px) {
    font-size: 0.82rem;
  }
`;

export const TopicDescription = styled.span`
  color: var(--text-muted);
  font-size: 0.75rem;
`;

export const TopicArrow = styled.span`
  font-size: 1.2rem;
  color: var(--border-strong);
  transition: transform 0.25s ease;
  flex-shrink: 0;

  ${TopicCard}:hover & {
    transform: translateX(6px);
    color: var(--accent);
  }

  @media screen and (max-width: 768px) {
    display: none;
  }
`;

export const TopActions = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-bottom: 20px;
  flex-wrap: wrap;
  gap: 0.5rem;

  @media screen and (max-width: 768px) {
    justify-content: stretch;
    margin-bottom: 12px;

    & > * {
      width: 100%;
    }
  }
`;

export const StartButton = styled.button`
  width: auto;
  min-width: 140px;
  height: auto;
  min-height: 40px;
  padding: 0.45rem 0.85rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 20px;
  border: 1px solid
    ${({ $started }) => ($started ? "var(--success)" : "var(--accent-soft)")};
  background: ${({ $started }) =>
    $started
      ? "color-mix(in srgb, var(--success) 25%, transparent)"
      : "var(--accent-soft)"};
  color: ${({ $started }) => ($started ? "var(--success)" : "var(--accent)")};
  font-size: 0.85rem;
  font-weight: 600;
  letter-spacing: 0.3px;
  cursor: ${({ $started }) => ($started ? "default" : "pointer")};
  transition: all 0.25s ease;
  backdrop-filter: blur(6px);

  &:hover {
    ${({ $started }) =>
      !$started &&
      `
      background: var(--accent-soft);
      border-color: var(--accent);
      transform: translateY(-1px);
    `}
  }

  &:active {
    ${({ $started }) =>
      !$started &&
      `
      transform: scale(0.97);
    `}
  }

  @media screen and (max-width: 768px) {
    width: 100%;
    max-width: 100%;
    min-width: 0;
  }
`;
